// TagMultiSelect — generic, reusable tag/chip picker for attaching a list of
// id-labelled options to something (a PO budget line's rate items,
// milestones, or SOWs). Purely controlled: selectedIds + onChange(ids) is
// the whole contract, so any caller can wire it to whatever underlying
// entity the tags represent. Selected ids render as removable badge chips
// (resolved to their label via `options`); an Add button expands an IN-FLOW
// checklist panel listing EVERY option — selected ones checked (unticking
// removes), the rest tickable — with a search input above the shared
// SOW-picker threshold so long registers (50+ milestones on prod contracts)
// stay navigable. In-flow (not an absolute popover) because the picker's
// only production home is inside Modal, whose panel is overflow-hidden: an
// absolute popover gets hard-clipped at the panel edges, while an expanding
// panel just grows the modal and scrolls with it (same reasoning as the
// variation-target picker's inline checklist).
//
// The component returns a FRAGMENT of two blocks — the trigger group
// (label + chips + Add button) and, when open, the checklist panel — so a
// grid parent can place the panel independently of the trigger's cell.
// PoLineEditor exploits this: its three pickers sit in a 3-column
// dense-flow grid and pass `panelClassName="col-span-full"`, so the open
// checklist spans the full line width (readable labels) while the triggers
// stay in their columns (dense flow backfills the top row, so a later
// trigger never drops below an earlier picker's panel). Selected chips cap
// at their column width and truncate their label (full text via title) —
// the badge base is whitespace-nowrap, and an uncapped long SOW/milestone
// name overflows the grid column, painting over the neighbouring picker's
// controls and carrying its own remove button out of reach.
//
// Options whose id appears in `usedElsewhere` (tagged on another budget
// line of the same PO) render greyed with the owning line as a hint, but
// stay tickable: tagging the same id on several lines is legitimate for
// every family. A per-unit rate item serves many lines, and a lump-sum
// milestone or SOW is split across lines whenever a variation increases its
// value and the extra goes on a new line — the invoice form then splits the
// payment across those lines (financeCalc's payables model suggests every
// tagging line). The greying is therefore a "look before you tag" cue, not
// a lock — and because the row stays an active control it keeps AA contrast
// (slate-500 on white / slate-400 on dark), muted relative to its
// neighbours rather than disabled-grey; the hint reads "also on Line 80"
// so the cue explains itself. The checkbox carries `data-used-elsewhere` so
// tests assert the state rather than a class name. An option selected on
// THIS line renders as a plain checked row whatever other lines do, so
// removal is always obvious. The
// whole control is wrapped in an accessible group so the `label` prop
// names the chip list and the add control for assistive tech.

import { X, Plus } from "lucide-react";
import { clsx } from "clsx";
import { useEffect, useId, useRef, useState } from "react";
import { formatAud } from "../../../utils/financeCalc";
import {
  SOW_SEARCH_THRESHOLD,
  matchesSowSearch,
} from "../tracking/shared/sowPickerSearch";

export interface TagOption {
  id: string;
  label: string;
  /** Ex-GST value shown in the checklist; null = variable/unpriced. */
  value?: number | null;
  /** "total" = lump sum (milestone/SOW); "unitRate" = per-unit price (rate item). */
  valueKind?: "total" | "unitRate";
}

// Formats an option's ex-GST value for the checklist row: options with no
// value data render label-only, a null value flags an unpriced/variable
// item, and unit-rate items get a "/ unit" suffix to distinguish them from
// lump-sum totals.
function optionLabel(o: TagOption): string {
  if (o.value === undefined) return o.label;
  if (o.value === null) return `${o.label} — variable`;
  const suffix = o.valueKind === "unitRate" ? " / unit" : "";
  return `${o.label} — ${formatAud(o.value)}${suffix}`;
}

interface TagMultiSelectProps {
  label: string;
  options: TagOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Option ids already tagged on another budget line of the same PO,
   *  mapped to a short owning-line hint (e.g. "Line 20"). */
  usedElsewhere?: ReadonlyMap<string, string>;
  /** Extra classes for the checklist panel block — the panel is a sibling
   *  of the trigger group (fragment return), so a grid parent can span it
   *  across columns (PoLineEditor passes "col-span-full"). */
  panelClassName?: string;
}

export default function TagMultiSelect({
  label,
  options,
  selectedIds,
  onChange,
  disabled,
  usedElsewhere,
  panelClassName,
}: TagMultiSelectProps) {
  const groupLabelId = useId();
  const checkboxIdPrefix = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const labelById = new Map(options.map((o) => [o.id, o.label]));

  const searchActive = options.length > SOW_SEARCH_THRESHOLD;
  const visibleOptions = searchActive
    ? options.filter((o) => matchesSowSearch(o.label, search))
    : options;

  // Reopening starts unfiltered — a stale query from the last visit would
  // silently hide options. Render-time prev-value guard (not an effect)
  // per the react-hooks set-state-in-effect rule.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) setSearch("");
  }

  useEffect(() => {
    if (!open) return;
    // Escape must dismiss ONLY this panel, not the Modal hosting it: the
    // Modal's focus trap listens for Escape (bubble phase, on its panel) and
    // would close the whole form, dropping every in-progress edit. Capture
    // phase + stopPropagation wins that race and stops the event before the
    // trap sees it.
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    // The panel is a DOM sibling of the trigger group (fragment return), so
    // outside-click must check both refs — a click inside the checklist is
    // not "outside" even though it's outside containerRef.
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey, true);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey, true);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const handleRemove = (id: string) => {
    onChange(selectedIds.filter((selectedId) => selectedId !== id));
  };

  return (
    <>
    <div
      role="group"
      aria-labelledby={groupLabelId}
      ref={containerRef}
      className="min-w-0"
    >
      <span id={groupLabelId} className="label">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-1.5">
        {selectedIds.map((id) => {
          const chipLabel = labelById.get(id) ?? id;
          return (
            <span
              key={id}
              title={chipLabel}
              className="badge-draft inline-flex max-w-full items-center gap-1"
            >
              <span className="min-w-0 truncate">{chipLabel}</span>
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${chipLabel}`}
                  className="-mr-1 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-600 dark:hover:text-slate-100"
                  onClick={() => handleRemove(id)}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}
        {!disabled && options.length > 0 && (
          <button
            type="button"
            aria-label={`Add to ${label}`}
            aria-haspopup="true"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-0.5 text-caption font-medium text-slate-600 hover:border-slate-400 hover:text-slate-800 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-slate-600 dark:text-slate-300 dark:hover:border-slate-500 dark:hover:text-slate-100"
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
            Add
          </button>
        )}
      </div>
    </div>
    {open && !disabled && options.length > 0 && (
        <div
          ref={panelRef}
          className={clsx(
            "rounded-md border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900/40",
            panelClassName,
          )}
        >
          {searchActive && (
            <input
              type="search"
              className="input mb-2 w-full"
              placeholder={`Search ${label.toLowerCase()}…`}
              aria-label={`Search ${label}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          {visibleOptions.length === 0 ? (
            <p className="px-2 py-1 text-caption text-slate-500 dark:text-slate-400">
              No options match &ldquo;{search.trim()}&rdquo;.
            </p>
          ) : (
            <ul className="max-h-52 space-y-1 overflow-y-auto">
              {visibleOptions.map((o) => {
                const selected = selectedIds.includes(o.id);
                const hint = usedElsewhere?.get(o.id);
                // Greyed (still tickable) only while unselected here: once
                // this line tags it too, it reads as an ordinary checked row.
                const usedOnOtherLine = !selected && hint !== undefined;
                const id = `${checkboxIdPrefix}-${o.id}`;
                return (
                  <li key={o.id}>
                    <label
                      htmlFor={id}
                      className={clsx(
                        "flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm hover:bg-slate-50 dark:hover:bg-slate-700",
                        usedOnOtherLine
                          ? "text-slate-500 dark:text-slate-400"
                          : "text-slate-700 dark:text-slate-200",
                      )}
                    >
                      <input
                        id={id}
                        type="checkbox"
                        checked={selected}
                        data-used-elsewhere={usedOnOtherLine ? "true" : undefined}
                        onChange={() => handleToggle(o.id)}
                        className={clsx(
                          "accent-brand-600",
                          usedOnOtherLine && "opacity-50",
                        )}
                      />
                      <span className="min-w-0 flex-1">{optionLabel(o)}</span>
                      {hint !== undefined && (
                        <span className="shrink-0 text-caption text-slate-500 dark:text-slate-400">
                          also on {hint}
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
    )}
    </>
  );
}
